import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ConditionBuilder, { getLockedTableName } from './ConditionBuilder.component';
import ClientScopeSelector from './ClientScopeSelector.component';
import LetterTriggerSection from './LetterTriggerSection.component';
import TriggerFieldSelect from './TriggerFieldSelect.component';
import TaskPreviewCard from './TaskPreviewCard.component';
import {
  getAuthCommentRegardingOptions,
  getAuthCommentServiceOptions,
  isIdEqual,
} from 'modules/enumerations/enumerationHelpers';
import { TASK_TRIGGER_LETTERS_GROUP_ID } from 'modules/enumerations/enumerationIds';
import {
  buildCutoffCondition,
  buildLetterCondition,
  getLetterFields,
  getLetterOptions,
  isLetterRule,
  joinLetterConditions,
  splitLetterConditions,
} from '../letterRules';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  formControl: {
    minWidth: 250,
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
}));

const DATE_DATA_TYPES = ['DATE', 'DATETIME', 'DATETIME2', 'SMALLDATETIME'];
const LETTER_TASK_TYPE_TITLES = ['Letter', 'VendorLetter'];

const RuleFormComponent = ({
  ruleData,
  conditions,
  clients,
  validTriggerFields,
  triggerServices,
  segmentCapableServices,
  isSegmentProbeDone,
  allClients,
  letterTemplates,
  emailTemplates,
  isLoadingTriggerFields,
  onChange,
  onConditionsChange,
  onClientsChange,
  onFetchReloPolicies,
  onServiceLevelChange,
  onTriggerTypeChange,
  onSave,
  onCancel,
  isLoading,
  isEditMode,
  allEnums,
}) => {
  const classes = useStyles();
  const [localErrors, setLocalErrors] = useState({});

  const handleFieldChange = (field, value) => {
    onChange(field, value);
    // Clear error for this field
    if (localErrors[field]) {
      setLocalErrors({ ...localErrors, [field]: null });
    }
  };

    const taskTypes =
    allEnums?.taskTypes && allEnums.taskTypes.length > 0
      ? allEnums.taskTypes.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  // Trigger services come from the Task.TaskTriggerServiceGroup catalog (only active groups are offered, plus the
  // group an existing rule already points at, so a rule on a retired group still renders and can be deactivated).
  // At Segment Level only services that have at least one segment table are listed (probed through the existing
  // trigger-fields endpoint by the container); a service whose probe has not answered is kept visible.
  // The Letters group is only reached through the "Letter sent" trigger, which manages its conditions
  const activeServiceTypes = triggerServices
    .filter(s => s.isActive)
    .filter(s => !isIdEqual(s.taskTriggerServiceGroupId, TASK_TRIGGER_LETTERS_GROUP_ID))
    .filter(s => !ruleData.isSegmentLevel || !isSegmentProbeDone || segmentCapableServices[s.taskTriggerServiceGroupId] !== false)
    .map(s => ({ id: s.taskTriggerServiceGroupId, title: s.name }));
  const currentService = triggerServices.find(s => s.taskTriggerServiceGroupId === ruleData.triggerServiceId);
  const serviceTypes = currentService && !currentService.isActive
    ? [...activeServiceTypes, { id: currentService.taskTriggerServiceGroupId, title: `${currentService.name} (inactive)` }]
    : activeServiceTypes;

    const taskImportance =
    allEnums?.taskImportances && allEnums.taskImportances.length > 0
      ? allEnums.taskImportances.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

    const operators =
    allEnums?.comparrisonOperators && allEnums.comparrisonOperators.length > 0 && Array.isArray(allEnums.comparrisonOperators)
      ? allEnums.comparrisonOperators.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '', additionalField: '' }];

  // Service Category and Regarding share the authorization comment options; Regarding depends on the category
  const serviceCategoryOptions = getAuthCommentServiceOptions(allEnums?.authCommentService);
  const regardingOptions = getAuthCommentRegardingOptions(allEnums?.authCommentRegarding, ruleData.serviceId);
  const isOptionSelected = (options, id) => options.some(o => isIdEqual(o.id, id));

  const handleServiceCategoryChange = (value) => {
    const serviceId = value === '' ? null : value;
    if (serviceId === ruleData.serviceId) {
      return;
    }
    handleFieldChange('serviceId', serviceId);
    // The previous Regarding belongs to the old category; clear it so the user picks from the new list
    onChange('regardingId', null);
  };

  const isLetterTask = () => {
    const taskTypeTitle = allEnums?.taskTypes?.find(t => t.id === ruleData.taskTypeId)?.title;
    return LETTER_TASK_TYPE_TITLES.includes(taskTypeTitle);
  };

  // Due-date candidates: date-family fields only; at segment level, File fields or the locked segment table
  const getDueDateFields = () => {
    const lockedTable = ruleData.isSegmentLevel ? getLockedTableName(conditions, validTriggerFields) : null;
    const isDueDateCandidate = f =>
      DATE_DATA_TYPES.includes(f.dataType)
      && (f.isFileTable || !ruleData.isSegmentLevel || f.tableName === lockedTable);
    return validTriggerFields.filter(isDueDateCandidate);
  };

  const handleDueDateFieldChange = (validTriggerFieldId, field) => {
    handleFieldChange('dueDateTriggerFieldId', validTriggerFieldId);
    handleFieldChange('dueDateTriggerFieldName', field ? field.fieldName : null);
  };

  // Letter-sent rules: the form manages the Letter and Sent Date conditions; the rest are optional File conditions
  const isLetterMode = isLetterRule(ruleData);
  const { letterField, sentDateField } = getLetterFields(validTriggerFields);
  const { letterCondition, cutoffCondition, additionalConditions } = splitLetterConditions(conditions);
  const letterOptions = getLetterOptions(emailTemplates, letterCondition?.comparisonValue);
  const fileFields = validTriggerFields.filter(f => f.isFileTable);

  const updateLetterConditions = changes =>
    onConditionsChange(joinLetterConditions({ letterCondition, cutoffCondition, additionalConditions, ...changes }));

  const handleLetterChange = (letter) => {
    updateLetterConditions({ letterCondition: letter && letterField ? buildLetterCondition(letterField, letter.rootTemplateId) : null });
    if (localErrors.letter) {
      setLocalErrors({ ...localErrors, letter: null });
    }

    // The task's Service Category and Regarding default to the letter's while the rule has none
    const letterServiceId = letter && serviceCategoryOptions.find(o => isIdEqual(o.id, letter.serviceId))?.id;
    if (letterServiceId && ruleData.serviceId === null) {
      handleFieldChange('serviceId', letterServiceId);
      const letterRegardingId = getAuthCommentRegardingOptions(allEnums?.authCommentRegarding, letterServiceId)
        .find(o => isIdEqual(o.id, letter.regardingId))?.id;
      if (letterRegardingId) {
        handleFieldChange('regardingId', letterRegardingId);
      }
    }
  };

  const handleCutoffChange = (cutoffDate) => {
    updateLetterConditions({ cutoffCondition: cutoffDate && sentDateField ? buildCutoffCondition(sentDateField, cutoffDate) : null });
  };

  const handleAdditionalConditionsChange = (newConditions) => {
    updateLetterConditions({ additionalConditions: newConditions });
  };

  const validateForm = () => {
    const errors = {};

    if (!ruleData.ruleName) errors.ruleName = 'Task title is required';
    if (!ruleData.description) errors.description = 'Task instructions are required';
    if (ruleData.triggerServiceId === null) errors.triggerServiceId = 'Service is required';
    if (ruleData.taskTypeId === null) errors.taskTypeId = 'Task type is required';
    if (ruleData.taskImportanceId === null) errors.taskImportanceId = 'Importance is required';
    // A saved rule can hold a value these lists don't offer (e.g. picked from the old unfiltered lists)
    if (ruleData.serviceId === null) {
      errors.serviceId = 'Service category is required';
    } else if (!isOptionSelected(serviceCategoryOptions, ruleData.serviceId)) {
      errors.serviceId = 'Select a valid service category';
    }
    if (ruleData.regardingId === null) {
      errors.regardingId = 'Regarding is required';
    } else if (!isOptionSelected(regardingOptions, ruleData.regardingId)) {
      errors.regardingId = 'Select a regarding for this service category';
    }
    if (isLetterMode) {
      if (!letterCondition) {
        errors.letter = 'Select the letter that triggers this rule';
      } else if (!ruleData.dueDateTriggerFieldId) {
        errors.letter = 'The letter trigger fields did not load; reload the page and try again';
      }
      if (additionalConditions.some(c => !c.validTriggerFieldId || !c.comparisonOperatorId)) {
        errors.conditions = 'Every condition needs a table, a field and an operator';
      }
      if (!Number.isInteger(ruleData.dueDateDaysOffset) || ruleData.dueDateDaysOffset < 0) {
        errors.dueDateDaysOffset = 'Enter 0 or more days';
      }
    } else if (conditions.length === 0) {
      errors.conditions = 'At least one condition is required';
    } else if (conditions.some(c => !c.validTriggerFieldId || !c.comparisonOperatorId)) {
      errors.conditions = 'Every condition needs a table, a field and an operator';
    }
    if (isLetterTask() && !ruleData.letterTemplateId) {
      errors.letterTemplateId = 'Letter template is required for letter tasks';
    }
    if (!isLetterMode && ruleData.dueDateBasedOnTrigger && !ruleData.dueDateTriggerFieldId) {
      errors.dueDateTriggerFieldId = 'Trigger field is required when due date is based on trigger';
    }
    if (!ruleData.applyToAllClients && clients.length === 0) {
      errors.clients = 'At least one client must be selected for specific clients mode';
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = (activate) => {
    if (validateForm()) {
      onSave(activate);
    }
  };

  return (
    <Box className={classes.container}>
      <Typography variant="h4" className={classes.title}>
        {isEditMode ? 'Edit Task Rule' : 'Create Task Rule'}
      </Typography>

      {/* Step 1: Basic Rule Configuration */}
      <Paper className={classes.section}>
        <Typography variant="h6" gutterBottom>
          1. Basic Rule Configuration
        </Typography>

        <TextField
          label="Task Title"
          value={ruleData.ruleName}
          onChange={(e) => handleFieldChange('ruleName', e.target.value)}
          fullWidth
          required
          error={!!localErrors.ruleName}
          helperText={localErrors.ruleName || 'This is what RCs will see as the task name (e.g., "Call EE before delivery")'}
          size="small"
          style={{ marginBottom: 12 }}
        />

        <TextField
          label="Task Instructions"
          value={ruleData.description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          fullWidth
          multiline
          rows={2}
          required
          error={!!localErrors.description}
          helperText={localErrors.description || 'Instructions RCs will see when viewing the task (e.g., "Confirm delivery address and time window")'}
          size="small"
          style={{ marginBottom: 12 }}
        />

        <FormControl component="fieldset" style={{ marginBottom: 12 }}>
          <FormLabel component="legend">Trigger</FormLabel>
          <RadioGroup
            row
            value={isLetterMode ? 'letter' : 'conditions'}
            onChange={(e) => onTriggerTypeChange(e.target.value === 'letter')}
          >
            <FormControlLabel value="conditions" control={<Radio />} label="Field conditions" />
            <FormControlLabel value="letter" control={<Radio />} label="Letter sent" />
          </RadioGroup>
        </FormControl>

        {!isLetterMode && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!localErrors.triggerServiceId}>
                <InputLabel>Trigger Service</InputLabel>
                <Select
                  value={ruleData.triggerServiceId !== null ? ruleData.triggerServiceId : ''}
                  onChange={(e) => onServiceLevelChange('triggerServiceId', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select service</em>
                  </MenuItem>
                  {serviceTypes.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.title}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {localErrors.triggerServiceId
                    || (ruleData.isSegmentLevel
                      ? 'Segment Level lists only services that have segment tables'
                      : 'File Level lists every service')}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">Level</FormLabel>
                <RadioGroup
                  row
                  value={ruleData.isSegmentLevel.toString()}
                  onChange={(e) => onServiceLevelChange('isSegmentLevel', e.target.value === 'true')}
                >
                  <FormControlLabel value="false" control={<Radio />} label="File Level" />
                  <FormControlLabel value="true" control={<Radio />} label="Segment Level" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Step 2 & 3: Trigger Conditions */}
      {isLetterMode && (
        <>
          <LetterTriggerSection
            letterOptions={letterOptions}
            serviceEnum={allEnums?.authCommentService}
            regardingEnum={allEnums?.authCommentRegarding}
            selectedRootTemplateId={letterCondition?.comparisonValue}
            cutoffDate={cutoffCondition?.comparisonValue}
            onLetterChange={handleLetterChange}
            onCutoffChange={handleCutoffChange}
            error={localErrors.letter}
            isLoading={isLoadingTriggerFields}
          />
          <ConditionBuilder
            conditions={additionalConditions}
            onChange={handleAdditionalConditionsChange}
            validTriggerFields={fileFields}
            isLoadingFields={isLoadingTriggerFields}
            operators={operators}
            title="Additional Conditions (optional)"
            description="Optionally limit the rule with authorization file fields (for example VIP). The letter and every condition here must all be true."
          />
        </>
      )}
      {!isLetterMode && ruleData.triggerServiceId !== null && (
        <ConditionBuilder
          conditions={conditions}
          onChange={onConditionsChange}
          validTriggerFields={validTriggerFields}
          isLoadingFields={isLoadingTriggerFields}
          operators={operators}
          isSegmentLevel={ruleData.isSegmentLevel}
        />
      )}

      {localErrors.conditions && (
        <Typography color="error" variant="body2" style={{ marginTop: -16, marginBottom: 16 }}>
          {localErrors.conditions}
        </Typography>
      )}

      {/* Step 4: Task Configuration */}
      <Paper className={classes.section}>
        <Typography variant="h6" gutterBottom>
          2. Task Configuration
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!localErrors.taskTypeId}>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={ruleData.taskTypeId !== null ? ruleData.taskTypeId : ''}
                onChange={(e) => handleFieldChange('taskTypeId', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select task type</em>
                </MenuItem>
                {taskTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!localErrors.taskImportanceId}>
              <InputLabel>Task Importance</InputLabel>
              <Select
                value={ruleData.taskImportanceId !== null ? ruleData.taskImportanceId : ''}
                onChange={(e) => handleFieldChange('taskImportanceId', e.target.value)}
              >
                {taskImportance.map((level) => (
                  <MenuItem key={level.id} value={level.id}>
                    {level.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!localErrors.serviceId}>
              <InputLabel>Service Category</InputLabel>
              <Select
                value={ruleData.serviceId !== null ? ruleData.serviceId : ''}
                onChange={(e) => handleServiceCategoryChange(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select service</em>
                </MenuItem>
                {serviceCategoryOptions.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.title}
                  </MenuItem>
                ))}
              </Select>
              {localErrors.serviceId && <FormHelperText>{localErrors.serviceId}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!localErrors.regardingId}>
              <InputLabel>Regarding</InputLabel>
              <Select
                value={ruleData.regardingId !== null ? ruleData.regardingId : ''}
                onChange={(e) => handleFieldChange('regardingId', e.target.value === '' ? null : e.target.value)}
                disabled={ruleData.serviceId === null}
              >
                <MenuItem value="">
                  <em>Select regarding</em>
                </MenuItem>
                {regardingOptions.map((regarding) => (
                  <MenuItem key={regarding.id} value={regarding.id}>
                    {regarding.title}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {localErrors.regardingId || (ruleData.serviceId === null ? 'Select a service category first' : '')}
              </FormHelperText>
            </FormControl>
          </Grid>

          {isLetterTask() && (
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!localErrors.letterTemplateId}>
                <InputLabel>Letter Template</InputLabel>
                <Select
                  value={ruleData.letterTemplateId || ''}
                  onChange={(e) => handleFieldChange('letterTemplateId', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select letter template</em>
                  </MenuItem>
                  {letterTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Step 5: Due Date Configuration */}
      <Paper className={classes.section}>
        <Typography variant="h6" gutterBottom>
          3. Due Date Configuration
        </Typography>

        {/* Letter-sent rules are always due a number of days after the letter is sent */}
        {isLetterMode && (
          <TextField
            label="Days after the letter is sent"
            type="number"
            size="small"
            value={Number.isNaN(ruleData.dueDateDaysOffset) ? '' : ruleData.dueDateDaysOffset}
            onChange={(e) => handleFieldChange('dueDateDaysOffset', parseInt(e.target.value, 10))}
            inputProps={{ min: 0 }}
            error={!!localErrors.dueDateDaysOffset}
            helperText={
              localErrors.dueDateDaysOffset
              || (ruleData.dueDateDaysOffset === 0
                ? 'Due the day the letter is sent'
                : `Due ${ruleData.dueDateDaysOffset} day${ruleData.dueDateDaysOffset === 1 ? '' : 's'} after the letter is sent`)
            }
            style={{ width: 260 }}
          />
        )}

        {!isLetterMode && (
          <FormControl component="fieldset" size="small" style={{ marginBottom: 12 }}>
            <FormLabel component="legend">Based On</FormLabel>
            <RadioGroup
              value={ruleData.dueDateBasedOnTrigger.toString()}
              onChange={(e) => handleFieldChange('dueDateBasedOnTrigger', e.target.value === 'true')}
            >
              <FormControlLabel value="true" control={<Radio size="small" />} label="Trigger field date" />
              <FormControlLabel value="false" control={<Radio size="small" />} label="Task creation date" />
            </RadioGroup>
          </FormControl>
        )}

        {!isLetterMode && ruleData.dueDateBasedOnTrigger && (
          <Box style={{ marginBottom: 12 }}>
            <TriggerFieldSelect
              value={ruleData.dueDateTriggerFieldId}
              onChange={handleDueDateFieldChange}
              fields={getDueDateFields()}
              isLoading={isLoadingTriggerFields}
              label="Due Date Trigger Field"
              required
              helperText="Select which date field to use for calculating the due date"
            />
            {localErrors.dueDateTriggerFieldId && (
              <Typography color="error" variant="caption">
                {localErrors.dueDateTriggerFieldId}
              </Typography>
            )}
          </Box>
        )}

        {!isLetterMode && (
          <TextField
            label="Days Offset"
            type="number"
            size="small"
            value={ruleData.dueDateDaysOffset}
            onChange={(e) => handleFieldChange('dueDateDaysOffset', parseInt(e.target.value, 10))}
            helperText={
              ruleData.dueDateDaysOffset === 0
                ? 'Due on the same day'
                : ruleData.dueDateDaysOffset > 0
                ? `Due ${ruleData.dueDateDaysOffset} days AFTER`
                : `Due ${Math.abs(ruleData.dueDateDaysOffset)} days BEFORE`
            }
            style={{ width: 200 }}
          />
        )}
      </Paper>

      {/* Step 4: Client Scope */}
      <ClientScopeSelector
        applyToAllClients={ruleData.applyToAllClients}
        selectedClients={clients}
        allClients={allClients}
        reloPolicyId={ruleData.reloPolicyId}
        onChange={onClientsChange}
        onFetchReloPolicies={onFetchReloPolicies}
      />

      {localErrors.clients && (
        <Typography color="error" variant="body2" style={{ marginTop: -16, marginBottom: 16 }}>
          {localErrors.clients}
        </Typography>
      )}

      {/* Preview */}
      <TaskPreviewCard ruleData={ruleData} validTriggerFields={validTriggerFields} isLetterRule={isLetterMode} />

      {/* Save */}
      <Box className={classes.buttonGroup}>
        <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => handleSave(false)}
          disabled={isLoading}
        >
          Save as Inactive
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSave(true)}
          disabled={isLoading}
        >
          Save and Activate
        </Button>
      </Box>
    </Box>
  );
};

RuleFormComponent.propTypes = {
  ruleData: PropTypes.object.isRequired,
  conditions: PropTypes.array.isRequired,
  clients: PropTypes.array.isRequired,
  validTriggerFields: PropTypes.array,
  triggerServices: PropTypes.arrayOf(
    PropTypes.shape({
      taskTriggerServiceGroupId: PropTypes.string,
      name: PropTypes.string,
      sortOrder: PropTypes.number,
      isActive: PropTypes.bool,
    }),
  ),
  // taskTriggerServiceGroupId -> true when the service has at least one segment table (false when it has none)
  segmentCapableServices: PropTypes.object,
  isSegmentProbeDone: PropTypes.bool,
  allClients: PropTypes.array,
  letterTemplates: PropTypes.array,
  // Template summaries (GET api/templates) for the letter-sent trigger's letter picker
  emailTemplates: PropTypes.array,
  isLoadingTriggerFields: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onConditionsChange: PropTypes.func.isRequired,
  onClientsChange: PropTypes.func.isRequired,
  onFetchReloPolicies: PropTypes.func.isRequired,
  onServiceLevelChange: PropTypes.func.isRequired,
  onTriggerTypeChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isEditMode: PropTypes.bool,
  allEnums: PropTypes.object,
};

RuleFormComponent.defaultProps = {
  validTriggerFields: [],
  triggerServices: [],
  segmentCapableServices: {},
  isSegmentProbeDone: false,
  allClients: [],
  letterTemplates: [],
  emailTemplates: [],
  isLoadingTriggerFields: false,
  isLoading: false,
  isEditMode: false,
};

export default RuleFormComponent;
